import axios, { AxiosError } from 'axios';
import {
  KnowledgeBase,
  CreateKnowledgeBaseRequest,
  UpdateKnowledgeBaseRequest,
  EmbedCodeResponse,
  ChatMessageRequest,
  ChatMessageResponse,
  Conversation,
  Message,
  KnowledgeBaseGreeting,
  UpsertGreetingRequest,
  ToggleGreetingRequest,
  KnowledgeBaseMember,
  KnowledgeBaseInvitation,
  KnowledgeBaseAuditLog,
  UserNotice,
  InviteUserRequest,
  UpdateMemberRoleRequest,
  AcceptInvitationResponse,
  LLMEndpoint,
  CreateLLMEndpointRequest,
  UpdateLLMEndpointRequest,
} from '../types';
import { API_BASE_URL } from '../config/api';
import { auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// All tokens are now Firebase tokens
// Request interceptor - add authentication token and refresh if needed
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');
    
    if (token) {
      // Always try to refresh Firebase token if user is available
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        try {
          // Get fresh token (Firebase SDK handles caching and auto-refresh)
          token = await getIdToken(firebaseUser);
          localStorage.setItem('token', token);
        } catch (error) {
          console.error('Failed to refresh Firebase token:', error);
          // Continue with existing token, will fail on backend if expired
        }
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    
    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh Firebase token
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        try {
          const newToken = await getIdToken(firebaseUser, true); // Force refresh
          localStorage.setItem('token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Retry the original request with new token
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          // Fall through to clear token and redirect
        }
      }
      
      // Only redirect to login if user was previously authenticated AND
      // is currently on a route that actually requires auth. Public pages
      // (portal, /mcp, /try, /login, /register, etc.) should silently
      // clear the stale token and continue rendering.
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
        const path = window.location.pathname;
        const requiresAuth = path.startsWith('/dashboard') || path === '/checkout/success';
        if (requiresAuth) {
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Knowledge Base API
export const knowledgeBasesApi = {
  // List all knowledge bases
  list: (): Promise<KnowledgeBase[]> => 
    api.get('/api/knowledge-bases').then(res => res.data),
  
  // Create a new knowledge base
  create: (data: CreateKnowledgeBaseRequest): Promise<KnowledgeBase> => 
    api.post('/api/knowledge-bases', data).then(res => res.data),
  
  // Get a specific knowledge base
  get: (id: string): Promise<KnowledgeBase> => 
    api.get(`/api/knowledge-bases/${id}`).then(res => res.data),
  
  // Update a knowledge base
  update: (id: string, data: UpdateKnowledgeBaseRequest): Promise<KnowledgeBase> => 
    api.post(`/api/knowledge-bases/${id}`, data).then(res => res.data),
  
  // Delete a knowledge base
  delete: (id: string): Promise<void> => 
    api.delete(`/api/knowledge-bases/${id}`).then(res => res.data),
  
  // Generate embed code
  generateEmbedCode: (id: string): Promise<EmbedCodeResponse> => 
    api.get(`/api/knowledge-bases/${id}/embed-code`).then(res => res.data),
};

export interface ConversationStats {
  total: number;
  active: number;
  closed: number;
  test: number;
  external: number;
}

// Conversations API
export const conversationsApi = {
  // Get conversations list (optionally filtered by knowledge base). The inbox
  // loads a page at a time via limit/offset ("load more").
  list: (knowledgeBaseId?: string, params?: { limit?: number; offset?: number }): Promise<Conversation[]> =>
    api.get('/api/conversations', {
      params: { ...(knowledgeBaseId ? { knowledge_base_id: knowledgeBaseId } : {}), ...params },
    }).then(res => res.data),

  // Aggregated counts for the KB overview card (server-side COUNT, uncapped).
  stats: (knowledgeBaseId: string): Promise<ConversationStats> =>
    api.get('/api/conversations/stats', { params: { knowledge_base_id: knowledgeBaseId } }).then(res => res.data),

  // Resolve a single conversation (used by email deep links to find its KB)
  get: (conversationId: string): Promise<{ id: string; knowledge_base_id: string; status: string }> =>
    api.get(`/api/conversations/${conversationId}`).then(res => res.data),

  // Get conversation messages
  getMessages: (conversationId: string): Promise<Message[]> =>
    api.get(`/api/conversations/${conversationId}/messages`).then(res => res.data),

  // Send manual response
  sendManualResponse: (conversationId: string, data: { message: string }): Promise<any> =>
    api.post(`/api/conversations/${conversationId}/respond`, data).then(res => res.data),

  // Claim the conversation (transitions to human_handling, assigns to caller)
  assign: (conversationId: string): Promise<{ status: string; assigned_user_id: string }> =>
    api.post(`/api/conversations/${conversationId}/assign`).then(res => res.data),

  // Change conversation status (close / active = hand back to bot / archived)
  updateStatus: (conversationId: string, status: string): Promise<{ status: string }> =>
    api.post(`/api/conversations/${conversationId}/status`, { status }).then(res => res.data),
};

// Per-Knowledge-Base conversation engagement settings (idle close window,
// contact-capture triggers). Channel-agnostic: applies to widget and LINE.
export interface ConversationSettings {
  idle_close_minutes: number;
  pre_chat_form: 'off' | 'optional' | 'required';
  ask_after_first_answer: boolean;
  session_end_invite: boolean;
  idle_close_invite: boolean;
  // Ask when the bot can't answer. Independent of the KB's human-handoff
  // switch: collecting a lead costs no agent seat.
  bot_unable_invite: boolean;
}

export const conversationSettingsApi = {
  get: (knowledgeBaseId: string): Promise<ConversationSettings> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/conversation-settings`).then(res => res.data),

  update: (knowledgeBaseId: string, settings: ConversationSettings): Promise<ConversationSettings> =>
    api.put(`/api/knowledge-bases/${knowledgeBaseId}/conversation-settings`, settings).then(res => res.data),
};

// Contact profiles (leads) — one per visitor per Knowledge Base.
export interface ContactProfile {
  id: string;
  visitor_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  channel: string;
  channel_identity: { line_user_id?: string; line_display_name?: string } | null;
  conversation_count: number;
  first_seen_at: string;
  last_seen_at: string;
}

export interface ConversationContactInfo {
  contact: ContactProfile | null;
  conversations: { id: string; status: string; created_at: string; updated_at: string }[];
}

export const contactsApi = {
  list: (
    knowledgeBaseId: string,
    params?: { search?: string; limit?: number; offset?: number },
  ): Promise<{ contacts: ContactProfile[]; total: number }> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/contacts`, { params }).then(res => res.data),

  exportCsv: (knowledgeBaseId: string): Promise<Blob> =>
    api
      .get(`/api/knowledge-bases/${knowledgeBaseId}/contacts/export`, { responseType: 'blob' })
      .then(res => res.data),

  forConversation: (conversationId: string): Promise<ConversationContactInfo> =>
    api.get(`/api/conversations/${conversationId}/contact`).then(res => res.data),
};

export interface UnansweredQuestion {
  question: string;
  frequency: number;
  first_asked_at: string;
  last_asked_at: string;
}

export const unansweredApi = {
  list: (
    knowledgeBaseId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{ questions: UnansweredQuestion[]; total: number }> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/unanswered`, { params }).then(res => res.data),

  draft: (
    knowledgeBaseId: string,
    question: string,
  ): Promise<{ draft_answer: string; used_entry_ids: string[] }> =>
    api
      .post(`/api/knowledge-bases/${knowledgeBaseId}/unanswered/draft`, { question })
      .then(res => res.data),

  /** Creates the entry AND marks the question handled — one call, never two. */
  answer: (
    knowledgeBaseId: string,
    data: { question: string; content: string; title?: string; category?: string },
  ): Promise<{ entry_id: string }> =>
    api
      .post(`/api/knowledge-bases/${knowledgeBaseId}/unanswered/answer`, data)
      .then(res => res.data),
};

// Knowledge Base Chat API
export const knowledgeBaseChatApi = {
  // Send a message to knowledge base
  sendMessage: (apiKey: string, data: ChatMessageRequest): Promise<ChatMessageResponse> => 
    api.post(`/api/knowledge-base-chat/${apiKey}`, data).then(res => res.data),
};

// Greeting API
export const greetingsApi = {
  // Get knowledge base greeting
  get: (knowledgeBaseId: string): Promise<KnowledgeBaseGreeting | null> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/greeting`).then(res => res.data),
  
  // Create or update greeting
  upsert: (knowledgeBaseId: string, data: UpsertGreetingRequest): Promise<void> =>
    api.put(`/api/knowledge-bases/${knowledgeBaseId}/greeting`, data).then(res => res.data),
  
  // Delete greeting
  delete: (knowledgeBaseId: string): Promise<void> =>
    api.delete(`/api/knowledge-bases/${knowledgeBaseId}/greeting`).then(res => res.data),
  
  // Toggle greeting enabled/disabled
  toggle: (knowledgeBaseId: string, data: ToggleGreetingRequest): Promise<void> =>
    api.post(`/api/knowledge-bases/${knowledgeBaseId}/greeting/toggle`, data).then(res => res.data),
  
  // Regenerate translations
  regenerate: (knowledgeBaseId: string): Promise<void> =>
    api.post(`/api/knowledge-bases/${knowledgeBaseId}/greeting/regenerate`).then(res => res.data),
};

// Knowledge Base Members API
export const knowledgeBaseMembersApi = {
  // Get knowledge base members
  list: (knowledgeBaseId: string): Promise<KnowledgeBaseMember[]> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/members`).then(res => res.data),
  
  // Invite user to knowledge base
  invite: (knowledgeBaseId: string, data: InviteUserRequest): Promise<KnowledgeBaseInvitation> =>
    api.post(`/api/knowledge-bases/${knowledgeBaseId}/members/invite`, data).then(res => res.data),
  
  // Remove member from knowledge base
  remove: (knowledgeBaseId: string, userId: string): Promise<void> =>
    api.delete(`/api/knowledge-bases/${knowledgeBaseId}/members/${userId}`).then(res => res.data),
  
  // Update member role
  updateRole: (knowledgeBaseId: string, userId: string, data: UpdateMemberRoleRequest): Promise<KnowledgeBaseMember> =>
    api.post(`/api/knowledge-bases/${knowledgeBaseId}/members/${userId}/role`, data).then(res => res.data),
};

// Invitations API
export const invitationsApi = {
  // Get my pending invitations
  getMyInvitations: (): Promise<KnowledgeBaseInvitation[]> =>
    api.get('/api/invitations').then(res => res.data),
  
  // Get invitation details (public)
  getInvitationDetails: (token: string): Promise<KnowledgeBaseInvitation> =>
    api.get(`/api/invitations/public/${token}`).then(res => res.data),
  
  // Accept invitation
  accept: (token: string): Promise<AcceptInvitationResponse> =>
    api.post(`/api/invitations/${token}/accept`).then(res => res.data),
};

// User notices API (in-app notices, e.g. "you were removed from a KB")
export const noticesApi = {
  list: (): Promise<UserNotice[]> =>
    api.get('/api/notices').then(res => res.data),
  dismiss: (id: string): Promise<void> =>
    api.post(`/api/notices/${id}/dismiss`).then(() => undefined),
};

// Knowledge Base Audit Logs API
export const knowledgeBaseAuditApi = {
  // Get audit logs for knowledge base
  list: (
    knowledgeBaseId: string, 
    params?: { limit?: number; offset?: number; action_type?: string; resource_type?: string }
  ): Promise<KnowledgeBaseAuditLog[]> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/audit-logs`, { params }).then(res => res.data),
};

// LLM Endpoints API
export const llmEndpointsApi = {
  // List all LLM endpoints for current user
  list: (): Promise<{ endpoints: LLMEndpoint[] }> =>
    api.get('/api/user/llm-endpoints').then(res => res.data),
  
  // Get a specific LLM endpoint
  get: (id: string): Promise<LLMEndpoint> =>
    api.get(`/api/user/llm-endpoints/${id}`).then(res => res.data),
  
  // Create a new LLM endpoint
  create: (data: CreateLLMEndpointRequest): Promise<LLMEndpoint> =>
    api.post('/api/user/llm-endpoints', data).then(res => res.data),
  
  // Update an LLM endpoint
  update: (id: string, data: UpdateLLMEndpointRequest): Promise<LLMEndpoint> =>
    api.put(`/api/user/llm-endpoints/${id}`, data).then(res => res.data),
  
  // Test an LLM endpoint
  test: (id: string): Promise<{ success: boolean; test_status: string; test_error?: string; is_active: boolean }> =>
    api.post(`/api/user/llm-endpoints/${id}/test`).then(res => res.data),
  
  // Set an endpoint as default
  setDefault: (id: string): Promise<LLMEndpoint> =>
    api.post(`/api/user/llm-endpoints/${id}/set-default`).then(res => res.data),
  
  // Clear default endpoint (use system default)
  clearDefault: (): Promise<{ success: boolean; message: string }> =>
    api.post('/api/user/llm-endpoints/clear-default').then(res => res.data),
  
  // Delete an LLM endpoint
  delete: (id: string): Promise<void> =>
    api.delete(`/api/user/llm-endpoints/${id}`).then(() => undefined),
};

// Per-user MCP tokens. Plaintext is returned only by create() — once.
export interface McpTokenSummary {
  id: string;
  name: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface CreateMcpTokenResponse {
  id: string;
  name: string;
  token: string; // plaintext, returned ONCE
  created_at: string;
}

export const mcpTokensApi = {
  list: (): Promise<McpTokenSummary[]> =>
    api.get('/api/users/me/mcp-tokens').then(res => res.data),

  create: (name: string): Promise<CreateMcpTokenResponse> =>
    api.post('/api/users/me/mcp-tokens', { name }).then(res => res.data),

  revoke: (id: string): Promise<void> =>
    api.delete(`/api/users/me/mcp-tokens/${id}`).then(() => undefined),
};

// Knowledge-base subscriptions (UGC "follow" relation).
export interface KbSubscription {
  knowledge_base_id: string;
  knowledge_base_name: string;
  knowledge_base_description: string | null;
  knowledge_base_slug: string | null;
  source: 'search' | 'qr_code' | 'shared_link';
  created_at: string;
}

export const subscriptionsApi = {
  subscribe: (
    knowledgeBaseId: string,
    source: 'search' | 'qr_code' | 'shared_link' = 'search',
  ): Promise<KbSubscription> =>
    api
      .post(`/api/knowledge-bases/${knowledgeBaseId}/subscribe`, { source })
      .then(res => res.data),

  unsubscribe: (knowledgeBaseId: string): Promise<void> =>
    api
      .delete(`/api/knowledge-bases/${knowledgeBaseId}/subscribe`)
      .then(() => undefined),

  listMine: (): Promise<KbSubscription[]> =>
    api.get('/api/users/me/subscriptions').then(res => res.data),
};

// Public discovery — no auth needed; safe to call with or without a token.
export interface DiscoverableKb {
  id: string;
  name: string;
  description: string | null;
  slug: string | null;
  created_at: string;
  subscriber_count: number;
}

export interface DiscoverListResponse {
  items: DiscoverableKb[];
  total: number;
}

/** Share-page payload — the KB metadata plus its public widget API key so the
 *  /kb/:slug page can mount an inline Q&A panel. */
export interface KbLandingData extends DiscoverableKb {
  /** False = owner paused sharing: page renders info + CTA, no Q&A. */
  share_enabled: boolean;
  /** Public widget API key; null while sharing is disabled. */
  api_key: string | null;
  /** Up to 3 recent real visitor questions ("Others are asking…"). */
  recent_questions: string[];
  /** Original source material (files + web links) shown below the Q&A panel.
   *  Empty unless the owner enabled source sharing. */
  sources: KbLandingSource[];
  /** True when a maintainer manually created/corrected entries — drives a
   *  "the owner has refined some answers" trust note. */
  has_manual_curation: boolean;
}

/** One shareable source on the /kb/:slug page. */
export interface KbLandingSource {
  id: string;
  name: string;
  /** 'file' → open/download via the download endpoint; 'link' → open `url`. */
  kind: 'file' | 'link';
  /** Set only for kind='link' (the original web source URL). */
  url?: string | null;
}

export const discoverApi = {
  list: (params?: { q?: string; limit?: number; offset?: number }): Promise<DiscoverListResponse> =>
    api.get('/api/discover/knowledge-bases', { params }).then(res => res.data),

  bySlug: (slug: string, language?: string, visitorId?: string): Promise<KbLandingData> =>
    api
      .get(`/api/discover/knowledge-bases/by-slug/${encodeURIComponent(slug)}`, {
        // visitor_id lets the backend drop the viewer's own conversations
        // from the "Others are asking…" prompts.
        params: { language: language || undefined, visitor_id: visitorId || undefined },
      })
      .then(res => res.data),
};

// KB share-page beacon (no auth, fire-and-forget)
export const kbLandingApi = {
  event: (
    slug: string,
    data: { event_type: 'view' | 'cta_click'; visitor_id?: string; referrer?: string; language?: string },
  ): Promise<void> =>
    axios.post(`${API_BASE_URL}/api/kb-landing/${encodeURIComponent(slug)}/event`, data).then(() => undefined),
};

// User preferences — `/api/users/me` is partial-update; default-KB has its own endpoint.
export interface UserPreferences {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  privacy_default: 'public' | 'anonymous';
  default_knowledge_base_id: string | null;
  /** Persisted UI/email language preference ('en-US'|'ja-JP'|'zh-CN'), or null if never set. */
  language: string | null;
}

export const userPreferencesApi = {
  get: (): Promise<UserPreferences> =>
    api.get('/api/users/me/preferences').then(res => res.data),

  update: (payload: {
    name?: string;
    avatar_url?: string;
    privacy_default?: 'public' | 'anonymous';
    language?: string;
  }): Promise<UserPreferences> =>
    api.patch('/api/users/me', payload).then(res => res.data),

  setDefaultKnowledgeBase: (knowledgeBaseId: string): Promise<void> =>
    api
      .put('/api/users/me/default-knowledge-base', { knowledge_base_id: knowledgeBaseId })
      .then(() => undefined),

  clearDefaultKnowledgeBase: (): Promise<void> =>
    api.delete('/api/users/me/default-knowledge-base').then(() => undefined),
};

// Trial API (public + authenticated)
export const trialApi = {
  // Page-view beacon fired when /try loads (no auth, fire-and-forget)
  visit: (data: { referrer?: string; language?: string; src?: string; from_path?: string }): Promise<void> =>
    axios.post(`${API_BASE_URL}/api/v1/trial/visit`, data).then(() => undefined),

  // Create anonymous trial (no auth needed)
  create: (data: { name?: string; language?: string; content?: string; mode?: 'text' | 'file' | 'web'; src?: string; from_path?: string }): Promise<{
    trial_token: string;
    import_code: string;
    knowledge_base_id: string;
    expires_at: string;
    entry_count: number;
  }> => axios.post(`${API_BASE_URL}/api/v1/trial`, data).then(res => res.data),

  // Claim trial knowledge base (requires Firebase auth)
  claim: (importCode: string): Promise<{
    knowledge_base_id: string;
    name: string;
    api_key: string;
    entry_count: number;
  }> => api.post('/api/v1/trial/claim', { import_code: importCode }).then(res => res.data),
};

// Free tools (/tools marketing pages) — public, no auth.
export interface FaqPair {
  question: string;
  answer: string;
}

export interface CrawlPreviewResult {
  title: string | null;
  urls: string[];
  total_discovered: number;
  from_sitemap: number;
  from_links: number;
}

export type WritingToolName = 'keigo-converter' | 'apology-mail' | 'inquiry-reply' | 'proofreader';

export type CrawlerToolName = 'ogp-checker' | 'link-checker' | 'sitemap-generator';

export type CompareName = 'compare-overseas' | 'line-feature' | 'compare-enterprise';

export type PdfToolName = 'pdf-summarizer' | 'pdf-to-text' | 'pdf-to-markdown';

export type ToolName =
  | 'index'
  | 'faq-generator'
  | 'chatbot-roi'
  | 'crawl-preview'
  | WritingToolName
  | CrawlerToolName
  | PdfToolName
  | CompareName;

export interface OgpCheckResult {
  final_url: string;
  title: string | null;
  description: string | null;
  canonical: string | null;
  favicon: string | null;
  og: {
    title: string | null;
    description: string | null;
    image: string | null;
    url: string | null;
    site_name: string | null;
    type: string | null;
  };
  twitter: {
    card: string | null;
    title: string | null;
    description: string | null;
    image: string | null;
  };
}

export interface LinkCheckReport {
  title: string | null;
  checked: number;
  broken: number;
  total_discovered: number;
  results: { url: string; status: number | null; ok: boolean }[];
}

export interface SitemapGenResult {
  xml: string;
  url_count: number;
  pages_fetched: number;
  truncated: boolean;
}

/** Covers a 20 MB upload on a slow uplink plus extraction; see pdfExtract. */
const PDF_TOOL_TIMEOUT_MS = 900000;

const uploadProgressHandler = (cb?: (percent: number) => void) =>
  cb
    ? (e: { loaded: number; total?: number }) =>
        cb(e.total ? Math.min(100, Math.round((e.loaded / e.total) * 100)) : 0)
    : undefined;

export const toolsApi = {
  faqGenerate: (data: { text?: string; url?: string }): Promise<{ faqs: FaqPair[] }> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/faq-generate`, data, { timeout: 120000 }).then(res => res.data),

  crawlPreview: (url: string): Promise<CrawlPreviewResult> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/crawl-preview`, { url }, { timeout: 60000 }).then(res => res.data),

  // Business-writing tool family (keigo / apology / inquiry-reply / proofread)
  write: (data: { tool: WritingToolName; text: string; context?: string }): Promise<{ result: string; notes?: string[] }> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/write`, data, { timeout: 120000 }).then(res => res.data),

  // Crawler trio (no LLM)
  ogpCheck: (url: string): Promise<OgpCheckResult> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/ogp-check`, { url }, { timeout: 30000 }).then(res => res.data),

  linkCheck: (url: string): Promise<LinkCheckReport> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/link-check`, { url }, { timeout: 90000 }).then(res => res.data),

  sitemapGenerate: (url: string): Promise<SitemapGenResult> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/sitemap-generate`, { url }, { timeout: 120000 }).then(res => res.data),

  // PDF tools (extract has no LLM; summarize is quota-walled).
  //
  // The timeout has to cover the UPLOAD, not just the work: a 12 MB PDF on a
  // 1 Mbps uplink spends ~2 minutes on the wire before the server sees a byte,
  // while extraction itself is seconds. The old 180 s budget was consumed
  // entirely by that upload and the request died the moment parsing started.
  // onUploadProgress lets the page show the real phase instead of a spinner.
  pdfExtract: (
    file: File,
    mode: 'markdown' | 'text',
    onUploadProgress?: (percent: number) => void,
  ): Promise<{ content: string; chars: number }> => {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    return axios
      .post(`${API_BASE_URL}/api/v1/tools/pdf-extract`, form, {
        timeout: PDF_TOOL_TIMEOUT_MS,
        onUploadProgress: uploadProgressHandler(onUploadProgress),
      })
      .then(res => res.data);
  },

  pdfSummarize: (
    file: File,
    onUploadProgress?: (percent: number) => void,
  ): Promise<{ summary: string; truncated: boolean }> => {
    const form = new FormData();
    form.append('file', file);
    return axios
      .post(`${API_BASE_URL}/api/v1/tools/pdf-summarize`, form, {
        timeout: PDF_TOOL_TIMEOUT_MS,
        onUploadProgress: uploadProgressHandler(onUploadProgress),
      })
      .then(res => res.data);
  },

  // Failed-upload follow-up: attaches the visitor's contact details to the
  // failure record so we can tell them once their file parses. `failure_id` is
  // absent when the tool page gave up on its own (timeout / aborted upload) —
  // the backend then creates the record from what the client knows.
  pdfFailureContact: (data: {
    failure_id?: string | null;
    email: string;
    name?: string;
    note?: string;
    language?: string;
    tool?: PdfToolName;
    error_code?: string;
    file_name?: string;
    file_size?: number;
  }): Promise<void> =>
    axios
      .post(`${API_BASE_URL}/api/v1/tools/pdf-failure/contact`, data, { timeout: 30000 })
      .then(() => undefined),

  // Funnel beacon (fire-and-forget)
  event: (data: {
    tool: ToolName;
    event_type: 'view' | 'use' | 'cta';
    src?: string;
    referrer?: string;
    language?: string;
  }): Promise<void> =>
    axios.post(`${API_BASE_URL}/api/v1/tools/event`, data).then(() => undefined),
};

// Site-wide page-view beacon (fire-and-forget; the backend whitelists which
// paths are stored).
export const pageViewApi = {
  record: (data: {
    path: string;
    from_path?: string;
    src?: string;
    visitor_id?: string;
    referrer?: string;
    language?: string;
  }): void => {
    axios.post(`${API_BASE_URL}/api/v1/page-view`, data).catch(() => {});
  },
};

// Admin visits-and-conversion funnel.
export interface FunnelPageRow {
  path: string;
  views: number;
  visitors: number;
  try_arrivals: number;
  trials_created: number;
  claims: number;
  register_arrivals: number;
}

export interface FunnelSrcRow {
  src: string;
  try_arrivals: number;
  trials_created: number;
  claims: number;
}

export interface FunnelToolRow {
  tool: string;
  views: number;
  uses: number;
  ctas: number;
}

export interface FunnelData {
  days: number;
  pages: FunnelPageRow[];
  srcs: FunnelSrcRow[];
  tools: FunnelToolRow[];
}

export const adminFunnelApi = {
  get: (days: number): Promise<FunnelData> =>
    api.get('/api/admin/funnel', { params: { days } }).then(res => res.data),
};

// LINE channel integration. Reads return only whether each credential is set
// (never the plaintext); updates send credentials only when (re)entered.
export interface LineChannelStatus {
  configured: boolean;
  is_enabled: boolean;
  channel_id: string | null;
  webhook_url: string | null;
  secret_set: boolean;
  token_set: boolean;
}

export interface UpdateLineChannel {
  channel_secret?: string;
  channel_access_token?: string;
  is_enabled?: boolean;
}

export const channelsApi = {
  getLine: (knowledgeBaseId: string): Promise<LineChannelStatus> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/channels/line`).then(res => res.data),

  updateLine: (knowledgeBaseId: string, data: UpdateLineChannel): Promise<LineChannelStatus> =>
    api.put(`/api/knowledge-bases/${knowledgeBaseId}/channels/line`, data).then(res => res.data),

  disableLine: (knowledgeBaseId: string): Promise<LineChannelStatus> =>
    api.delete(`/api/knowledge-bases/${knowledgeBaseId}/channels/line`).then(res => res.data),
};

// Outbound webhook subscriptions (conversation event delivery)
export interface WebhookSubscription {
  id: string;
  url: string;
  event_types: string[];
  is_enabled: boolean;
  secret_set: boolean;
  created_at: string;
}

export const webhooksApi = {
  list: (knowledgeBaseId: string): Promise<WebhookSubscription[]> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/webhooks`).then(res => res.data.subscriptions),

  // Returns the signing secret in plaintext exactly once.
  create: (knowledgeBaseId: string, data: { url: string; event_types?: string[] }): Promise<{ id: string; secret: string }> =>
    api.post(`/api/knowledge-bases/${knowledgeBaseId}/webhooks`, data).then(res => res.data),

  remove: (knowledgeBaseId: string, subscriptionId: string): Promise<void> =>
    api.delete(`/api/knowledge-bases/${knowledgeBaseId}/webhooks/${subscriptionId}`).then(res => res.data),
};

// Fallback reply — message + contact form shown when the bot can't answer.
// "optional" | "required" | "hidden" per contact field.
export type ContactFieldMode = 'optional' | 'required' | 'hidden';

export interface FallbackReply {
  id: string;
  knowledge_base_id: string;
  source_language: string;
  fallback_message: string;
  contact_name_field: ContactFieldMode;
  contact_email_field: ContactFieldMode;
  contact_phone_field: ContactFieldMode;
  is_enabled: boolean;
  available_translations: string[];
  created_at: string;
  updated_at: string;
}

export interface UpsertFallbackReply {
  source_language: string;
  fallback_message: string;
  contact_name_field?: ContactFieldMode;
  contact_email_field?: ContactFieldMode;
  contact_phone_field?: ContactFieldMode;
}

export const fallbackApi = {
  get: (knowledgeBaseId: string): Promise<FallbackReply | null> =>
    api.get(`/api/knowledge-bases/${knowledgeBaseId}/fallback-reply`).then(res => res.data),

  upsert: (knowledgeBaseId: string, data: UpsertFallbackReply): Promise<void> =>
    api.put(`/api/knowledge-bases/${knowledgeBaseId}/fallback-reply`, data).then(res => res.data),

  delete: (knowledgeBaseId: string): Promise<void> =>
    api.delete(`/api/knowledge-bases/${knowledgeBaseId}/fallback-reply`).then(res => res.data),

  toggle: (knowledgeBaseId: string, isEnabled: boolean): Promise<void> =>
    api.post(`/api/knowledge-bases/${knowledgeBaseId}/fallback-reply/toggle`, { is_enabled: isEnabled }).then(res => res.data),
};

// ===========================================================================
// News / announcements (public read + admin authoring)
// ===========================================================================

export interface NewsTranslation {
  locale: string;
  title: string;
  summary?: string | null;
  body_md: string;
}

export interface PublicNewsListItem {
  id: string;
  slug?: string | null;
  locale: string;
  title: string;
  summary?: string | null;
  pinned: boolean;
  published_at?: string | null;
  updated_at: string;
}

export interface PublicNewsDetail extends PublicNewsListItem {
  body_md: string;
  cover_image_url?: string | null;
}

/** The one announcement currently promoted as the homepage strip, or null. */
export interface PublicNewsBanner {
  id: string;
  slug?: string | null;
  locale: string;
  title: string;
  summary?: string | null;
  banner_ends_at?: string | null;
}

export interface AdminNews {
  id: string;
  slug?: string | null;
  status: 'draft' | 'published' | 'archived';
  pinned: boolean;
  cover_image_url?: string | null;
  default_locale: string;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
  banner_enabled: boolean;
  banner_starts_at?: string | null;
  banner_ends_at?: string | null;
  translations: NewsTranslation[];
}

export interface UpsertNewsRequest {
  slug?: string | null;
  status?: 'draft' | 'published' | 'archived';
  pinned?: boolean;
  cover_image_url?: string | null;
  default_locale?: string;
  banner_enabled?: boolean;
  banner_starts_at?: string | null;
  banner_ends_at?: string | null;
  translations: NewsTranslation[];
}

export const newsApi = {
  // Public
  publicList: (locale?: string): Promise<PublicNewsListItem[]> =>
    api.get('/api/news', { params: locale ? { locale } : {} }).then(res => res.data),
  publicGet: (idOrSlug: string, locale?: string): Promise<PublicNewsDetail> =>
    api.get(`/api/news/${idOrSlug}`, { params: locale ? { locale } : {} }).then(res => res.data),
  activeBanner: (locale?: string): Promise<PublicNewsBanner | null> =>
    api.get('/api/news-banner', { params: locale ? { locale } : {} }).then(res => res.data),
  // Admin
  adminList: (): Promise<AdminNews[]> =>
    api.get('/api/admin/news').then(res => res.data),
  adminGet: (id: string): Promise<AdminNews> =>
    api.get(`/api/admin/news/${id}`).then(res => res.data),
  create: (data: UpsertNewsRequest): Promise<AdminNews> =>
    api.post('/api/admin/news', data).then(res => res.data),
  update: (id: string, data: UpsertNewsRequest): Promise<AdminNews> =>
    api.put(`/api/admin/news/${id}`, data).then(res => res.data),
  archive: (id: string): Promise<void> =>
    api.delete(`/api/admin/news/${id}`).then(() => undefined),
};

// ── Virtual LLM layer (admin) ──────────────────────────────────────────────
export interface LlmProvider {
  id: string;
  name: string;
  provider_key: string;
  api_url: string;
  default_model: string;
  is_active: boolean;
  has_key: boolean;
}
export interface CreateLlmProviderRequest {
  name: string;
  provider_key: string;
  api_url: string;
  default_model: string;
  api_key: string;
}
export interface UpdateLlmProviderRequest {
  name: string;
  api_url: string;
  default_model: string;
  is_active: boolean;
  api_key?: string; // empty/undefined = keep existing key
}
export interface LlmRoute {
  id: string;
  task: string;
  tier: string;
  provider_id: string;
  model: string | null;
  fallback_provider_id: string | null;
  fallback_model: string | null;
  is_active: boolean;
}
export interface SetLlmRouteRequest {
  task: string;
  tier: string;
  provider_id: string;
  model?: string | null;
  fallback_provider_id?: string | null;
  fallback_model?: string | null;
}
export interface LlmTestResult {
  success: boolean;
  error?: string;
}
export interface LlmProviderUsage {
  llm_provider: string | null;
  llm_model: string | null;
  query_count: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export const llmProvidersApi = {
  list: (): Promise<LlmProvider[]> =>
    api.get('/api/admin/llm-providers').then(res => res.data),
  create: (data: CreateLlmProviderRequest): Promise<LlmProvider> =>
    api.post('/api/admin/llm-providers', data).then(res => res.data),
  update: (id: string, data: UpdateLlmProviderRequest): Promise<LlmProvider> =>
    api.put(`/api/admin/llm-providers/${id}`, data).then(res => res.data),
  remove: (id: string): Promise<void> =>
    api.delete(`/api/admin/llm-providers/${id}`).then(() => undefined),
  test: (id: string): Promise<LlmTestResult> =>
    api.post(`/api/admin/llm-providers/${id}/test`).then(res => res.data),
};

export const llmRoutesApi = {
  list: (): Promise<LlmRoute[]> =>
    api.get('/api/admin/llm-routes').then(res => res.data),
  set: (data: SetLlmRouteRequest): Promise<LlmRoute> =>
    api.put('/api/admin/llm-routes', data).then(res => res.data),
  remove: (id: string): Promise<void> =>
    api.delete(`/api/admin/llm-routes/${id}`).then(() => undefined),
  usage: (days = 30): Promise<LlmProviderUsage[]> =>
    api.get('/api/admin/llm-usage', { params: { days } }).then(res => res.data),
};

// ===========================================================================
// Admin operations console (platform staff only)
// ===========================================================================

export interface AdminOverview {
  total_users: number;
  new_users_30d: number;
  paid_subscriptions: number;
  total_knowledge_bases: number;
  total_conversations: number;
  tokens_this_month: number;
}

export interface MonthCount { month: string; count: number; }
export interface GrowthMonth {
  month: string;
  new_users: number;
  total_users: number;
  new_paid_subs: number;
  total_paid_subs: number;
}
export interface RevenueMonth { month: string; currency: string; total: number; invoices: number; }
export interface MrrByCurrency { currency: string; mrr: number; subscriptions: number; }
export interface RevenueStats { monthly: RevenueMonth[]; mrr: MrrByCurrency[]; }
export interface MonthTokens { month: string; total_tokens: number; query_count: number; }
export interface TopUserTokens { user_id: string; email: string; name: string; total_tokens: number; query_count: number; }
export interface TokenStats { monthly: MonthTokens[]; top_users_this_month: TopUserTokens[]; }

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  created_at: string;
  plan?: string | null;
  subscription_status?: string | null;
  knowledge_base_count: number;
  tokens_this_month: number;
  prepaid_token_balance: number;
  is_admin: boolean;
  account_origin: 'self' | 'invited';
}
export interface AdminUserList { items: AdminUserRow[]; total: number; }

/** Complimentary plans a root admin can grant. Both convert only to/from Free. */
export type GrantablePlan = 'vip' | 'recovery';
export const GRANTABLE_PLANS: GrantablePlan[] = ['vip', 'recovery'];

export interface AdminUserDetail {
  id: string;
  email: string;
  name: string;
  created_at: string;
  plan?: string | null;
  subscription_status?: string | null;
  billing_interval?: string | null;
  current_period_end?: string | null;
  /** When an admin-granted plan reverts to Free. null = never. */
  manual_plan_expires_at?: string | null;
  stripe_customer_id?: string | null;
  prepaid_token_balance: number;
  tokens_this_month: number;
  queries_this_month: number;
  knowledge_base_count: number;
  conversation_count: number;
  referral_code?: string | null;
  referred_count: number;
  is_admin: boolean;
  admin_role?: string | null;
  account_origin: 'self' | 'invited';
}

export interface AdminEntry {
  user_id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
}

// ---- Phase 2: investigation surfaces ----

export interface AdminConversationRow {
  id: string;
  knowledge_base_id: string;
  knowledge_base_name: string;
  owner_email?: string | null;
  status: string;
  channel?: string | null;
  user_language?: string | null;
  visitor_id: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}
export interface AdminConversationList { items: AdminConversationRow[]; total: number; }
export interface AdminMessage {
  id: string;
  role: string;
  content: string;
  response_source?: string | null;
  confidence_score?: number | null;
  user_rating?: number | null;
  created_at: string;
}
export interface AdminConversationDetail extends Omit<AdminConversationRow, 'message_count' | 'owner_email'> {
  visitor_info?: unknown;
  messages: AdminMessage[];
}
export interface UnansweredRow {
  id: string;
  knowledge_base_id: string;
  knowledge_base_name: string;
  query_text: string;
  answer_text?: string | null;
  confidence_score?: number | null;
  source?: string | null;
  created_at: string;
}

export interface KbStorageRow {
  knowledge_base_id: string;
  name: string;
  owner_email?: string | null;
  file_count: number;
  source_count: number;
}
export interface StoredFile {
  job_id: string;
  s3_key: string;
  file_name: string;
  job_type: string;
  status: string;
  extracted_entries: number;
  error_message?: string | null;
  started_at: string;
  completed_at?: string | null;
}

export interface BillingDetail {
  user_id: string;
  email: string;
  name: string;
  prepaid_token_balance: number;
  subscription: {
    plan?: string | null;
    status?: string | null;
    billing_interval?: string | null;
    current_period_start?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean | null;
    canceled_at?: string | null;
    stripe_customer_id?: string | null;
    stripe_subscription_id?: string | null;
  };
  invoices: Array<{ stripe_invoice_id: string; number?: string | null; status: string; currency: string; total: number; amount_paid: number; hosted_invoice_url?: string | null; period_start?: string | null; period_end?: string | null; paid_at?: string | null; created_at: string; }>;
  payment_events: Array<{ event_type: string; stripe_object_id?: string | null; amount?: number | null; currency?: string | null; status?: string | null; error_message?: string | null; created_at: string; }>;
  prepaid_purchases: Array<{ tokens_granted: number; amount: number; currency: string; created_at: string; }>;
}

export interface ReferralRow {
  id: string;
  referrer_email?: string | null;
  referee_email?: string | null;
  referrer_code_used: string;
  status: string;
  referee_credit_granted: boolean;
  referrer_credit_granted: boolean;
  referrer_credit_skipped_reason?: string | null;
  reward_amount?: number | null;
  reward_currency?: string | null;
  created_at: string;
  qualified_at?: string | null;
  completed_at?: string | null;
}
export interface ReferralList { items: ReferralRow[]; total: number; by_status: Array<{ status: string; count: number }>; }

export interface AdminNote { note?: string | null; actor_email?: string | null; created_at: string; }

// /try usage tracking
export interface TrialDayCounts { day: string; visits: number; created: number; claimed: number; }
export interface TrialModeCount { mode: string; count: number; }
export interface TrialStats {
  days: number;
  visits: number;
  trials_created: number;
  claims: number;
  queries: number;
  modes: TrialModeCount[];
  daily: TrialDayCounts[];
}
export interface TrialSessionRow {
  trial_kb_id: string;
  created_at: string;
  last_activity: string;
  mode?: string | null;
  name?: string | null;
  source_summary?: string | null;
  query_count: number;
  status: 'claimed' | 'active' | 'expired';
  ip?: string | null;
}
export interface TrialSessionList { items: TrialSessionRow[]; total: number; }
export interface TrialEventRow {
  event_type: string;
  ip?: string | null;
  detail: Record<string, any>;
  created_at: string;
}

export interface AdminKbRow {
  id: string;
  name: string;
  owner_email: string | null;
  plan: string | null;
  created_at: string;
  is_trial: boolean;
  is_pitto_official: boolean;
  entry_count: number;
  conversations_30d: number;
  landing_enabled: boolean;
  slug: string | null;
  landing_views: number;
  landing_registrations: number;
}

export interface AdminKbList {
  items: AdminKbRow[];
  total: number;
}

export interface KbLandingDayCounts {
  day: string;
  views: number;
  cta_clicks: number;
  registrations: number;
}

export interface KbLandingStats {
  days: number;
  views: number;
  cta_clicks: number;
  registrations: number;
  daily: KbLandingDayCounts[];
}

// ── Kumamoto support campaign (/kumamoto) ──────────────────────────────────

export interface KumamotoFile {
  id: string;
  file_name: string;
  content_type?: string;
  size_bytes: number;
}

export interface KumamotoApplication {
  id: string;
  entity_name: string;
  contact_name: string;
  phone: string;
  location: 'kumamoto' | 'kyushu_other';
  kb_title: string;
  purpose: string;
  setup_proxy: boolean;
  website_url: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  approved_at: string | null;
  expires_at: string | null;
  submitted_at: string;
  files: KumamotoFile[];
}

export interface KumamotoState {
  application: KumamotoApplication | null;
  staged_files: KumamotoFile[];
}

export interface KumamotoSubmitRequest {
  entity_name: string;
  contact_name: string;
  phone: string;
  location: 'kumamoto' | 'kyushu_other';
  kb_title: string;
  purpose: string;
  setup_proxy: boolean;
  website_url?: string;
  agree_terms: boolean;
  file_ids: string[];
}

export const kumamotoApi = {
  getApplication: (): Promise<KumamotoState> =>
    api.get('/api/kumamoto/application').then(res => res.data),
  submit: (req: KumamotoSubmitRequest): Promise<{ id: string }> =>
    api.post('/api/kumamoto/application', req).then(res => res.data),
  uploadFile: (file: File | Blob, fileName: string, onProgress?: (pct: number) => void): Promise<KumamotoFile> => {
    const form = new FormData();
    form.append('file', file, fileName);
    return api
      .post('/api/kumamoto/files', form, {
        timeout: 120000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then(res => res.data);
  },
  deleteFile: (id: string): Promise<void> =>
    api.delete(`/api/kumamoto/files/${id}`).then(() => undefined),
  // Anonymous landing beacon — plain fetch, fire-and-forget (never blocks the page).
  recordView: (payload: { visitor_id?: string; src?: string; referrer?: string; language?: string }): void => {
    fetch(`${API_BASE_URL}/api/kumamoto/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'view', ...payload }),
    }).catch(() => undefined);
  },
};

export interface AdminKumamotoRow {
  id: string;
  submitted_at: string;
  entity_name: string;
  location: 'kumamoto' | 'kyushu_other';
  contact_name: string;
  status: 'pending' | 'approved' | 'rejected';
  setup_proxy: boolean;
  expires_at: string | null;
  user_email: string;
  file_count: number;
  duplicate_entity: boolean;
}

export interface AdminKumamotoList {
  items: AdminKumamotoRow[];
  total: number;
  counts: { pending: number; approved: number; rejected: number };
}

export interface AdminToolFailureRow {
  id: string;
  created_at: string;
  tool: string;
  error_code: string;
  /** 'server' = the extractor rejected it; 'client' = timeout / aborted upload. */
  source: string;
  file_name: string | null;
  file_size: number | null;
  has_file: boolean;
  contact_email: string | null;
  contact_name: string | null;
  contact_at: string | null;
  status: string;
  client_ip: string | null;
}

export interface AdminToolFailureList {
  items: AdminToolFailureRow[];
  total: number;
  by_status: Record<string, number>;
  by_error_code: Record<string, number>;
  awaiting_reply: number;
}

export interface AdminToolFailureDetail extends AdminToolFailureRow {
  error_detail: string | null;
  user_agent: string | null;
  language: string | null;
  contact_note: string | null;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by_email: string | null;
}

export interface AdminKumamotoStats {
  days: number;
  views: number;
  unique_visitors: number;
  by_src: { src: string; views: number; unique_visitors: number }[];
  daily: { day: string; views: number; unique_visitors: number }[];
  applications: { pending: number; approved: number; rejected: number };
}

export interface AdminKumamotoDetail extends AdminKumamotoRow {
  user_id: string;
  user_created_at: string;
  user_language: string | null;
  phone: string;
  kb_title: string;
  purpose: string;
  website_url: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  approved_by: string | null;
  history: Record<string, unknown>[];
  files: (KumamotoFile & { content_type: string; created_at: string })[];
  email_log: { email_type: string; recipient: string; success: boolean; error: string | null; created_at: string }[];
}

export const adminApi = {
  overview: (): Promise<AdminOverview> =>
    api.get('/api/admin/stats/overview').then(res => res.data),
  registrations: (): Promise<MonthCount[]> =>
    api.get('/api/admin/stats/registrations').then(res => res.data),
  growth: (): Promise<GrowthMonth[]> =>
    api.get('/api/admin/stats/growth').then(res => res.data),
  revenue: (): Promise<RevenueStats> =>
    api.get('/api/admin/stats/revenue').then(res => res.data),
  tokens: (): Promise<TokenStats> =>
    api.get('/api/admin/stats/tokens').then(res => res.data),
  users: (params?: { search?: string; limit?: number; offset?: number }): Promise<AdminUserList> =>
    api.get('/api/admin/users', { params }).then(res => res.data),
  userDetail: (id: string): Promise<AdminUserDetail> =>
    api.get(`/api/admin/users/${id}`).then(res => res.data),
  setUserOrigin: (id: string, origin: 'self' | 'invited'): Promise<void> =>
    api.put(`/api/admin/users/${id}/origin`, { account_origin: origin }).then(() => undefined),
  setUserPlan: (id: string, plan: GrantablePlan | 'free', expiresAt?: string | null): Promise<void> =>
    api.put(`/api/admin/users/${id}/plan`, { plan, expires_at: expiresAt ?? null }).then(() => undefined),
  listAdmins: (): Promise<AdminEntry[]> =>
    api.get('/api/admin/admins').then(res => res.data),
  grantAdmin: (email: string, role?: string): Promise<void> =>
    api.post('/api/admin/admins', { email, role }).then(() => undefined),
  revokeAdmin: (userId: string): Promise<void> =>
    api.delete(`/api/admin/admins/${userId}`).then(() => undefined),

  // Phase 2: conversation investigation
  conversations: (params?: { status?: string; knowledge_base_id?: string; search?: string; limit?: number; offset?: number }): Promise<AdminConversationList> =>
    api.get('/api/admin/conversations', { params }).then(res => res.data),
  conversationDetail: (id: string): Promise<AdminConversationDetail> =>
    api.get(`/api/admin/conversations/${id}`).then(res => res.data),
  unanswered: (params?: { threshold?: number; knowledge_base_id?: string; limit?: number }): Promise<UnansweredRow[]> =>
    api.get('/api/admin/conversations/unanswered', { params }).then(res => res.data),

  // Phase 2: S3 content browse
  storageKnowledgeBases: (): Promise<KbStorageRow[]> =>
    api.get('/api/admin/storage/knowledge-bases').then(res => res.data),
  storageFiles: (kbId: string): Promise<StoredFile[]> =>
    api.get(`/api/admin/storage/knowledge-bases/${kbId}/files`).then(res => res.data),
  storageObject: (key: string): Promise<Blob> =>
    api.get('/api/admin/storage/object', { params: { key }, responseType: 'blob' }).then(res => res.data),

  // Phase 2: billing investigation
  billingDetail: (userId: string): Promise<BillingDetail> =>
    api.get(`/api/admin/billing/${userId}`).then(res => res.data),

  // Phase 2: referral investigation
  referrals: (params?: { status?: string; limit?: number; offset?: number }): Promise<ReferralList> =>
    api.get('/api/admin/referrals', { params }).then(res => res.data),

  // Cross-tenant KB directory + share-page funnel
  knowledgeBases: (params?: { search?: string; limit?: number; offset?: number }): Promise<AdminKbList> =>
    api.get('/api/admin/knowledge-bases', { params }).then(res => res.data),
  kbLandingStats: (days?: number): Promise<KbLandingStats> =>
    api.get('/api/admin/knowledge-bases/landing-stats', { params: { days } }).then(res => res.data),

  // /try usage tracking
  trialStats: (days?: number): Promise<TrialStats> =>
    api.get('/api/admin/trials/stats', { params: { days } }).then(res => res.data),
  trials: (params?: { mode?: string; claimed?: boolean; limit?: number; offset?: number }): Promise<TrialSessionList> =>
    api.get('/api/admin/trials', { params }).then(res => res.data),
  trialEvents: (kbId: string): Promise<TrialEventRow[]> =>
    api.get(`/api/admin/trials/${kbId}/events`).then(res => res.data),

  // Kumamoto campaign review
  kumamotoList: (params?: { status?: string; limit?: number; offset?: number }): Promise<AdminKumamotoList> =>
    api.get('/api/admin/kumamoto', { params }).then(res => res.data),
  kumamotoStats: (days?: number): Promise<AdminKumamotoStats> =>
    api.get('/api/admin/kumamoto/stats', { params: { days } }).then(res => res.data),
  kumamotoDetail: (id: string): Promise<AdminKumamotoDetail> =>
    api.get(`/api/admin/kumamoto/${id}`).then(res => res.data),
  kumamotoApprove: (id: string): Promise<{ expires_at: string }> =>
    api.post(`/api/admin/kumamoto/${id}/approve`).then(res => res.data),
  kumamotoReject: (id: string, reason: string): Promise<void> =>
    api.post(`/api/admin/kumamoto/${id}/reject`, { reason }).then(() => undefined),
  kumamotoFile: (id: string, fileId: string): Promise<Blob> =>
    api.get(`/api/admin/kumamoto/${id}/files/${fileId}/download`, { responseType: 'blob', timeout: 120000 }).then(res => res.data),

  // Free-PDF-tool failure triage
  toolFailures: (params?: {
    status?: string;
    error_code?: string;
    with_contact?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<AdminToolFailureList> =>
    api.get('/api/admin/tool-failures', { params }).then(res => res.data),
  toolFailureDetail: (id: string): Promise<AdminToolFailureDetail> =>
    api.get(`/api/admin/tool-failures/${id}`).then(res => res.data),
  toolFailureUpdate: (id: string, status: string, resolution?: string): Promise<void> =>
    api.patch(`/api/admin/tool-failures/${id}`, { status, resolution }).then(() => undefined),
  toolFailureFile: (id: string): Promise<Blob> =>
    api
      .get(`/api/admin/tool-failures/${id}/download`, {
        params: { download: '1' },
        responseType: 'blob',
        timeout: 120000,
      })
      .then(res => res.data),

  // Shared tracking notes
  notes: (targetType: string, targetId: string): Promise<AdminNote[]> =>
    api.get('/api/admin/notes', { params: { target_type: targetType, target_id: targetId } }).then(res => res.data),
  addNote: (targetType: string, targetId: string, note: string): Promise<void> =>
    api.post('/api/admin/notes', { target_type: targetType, target_id: targetId, note }).then(() => undefined),
};

export default api;